import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react'
import styles from './Login.module.scss'
import {
  Button,
  ButtonType,
  Form,
  FormGroup,
  Input,
  PageLevelBanner,
  PageLevelBannerType,
  Spinner
} from '@digicert/dcone-common-ui'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { getDecodedState, getValueFromState, isOauthFlow, showCreateOneLink, verify } from './Login.actions'
import Cookies from 'js-cookie'
import { DCONE_LOCALE_COOKIE_NAME, USER_NAME_REGEXP } from 'utils/constants'
import { forgotUserName, password } from 'utils/routes'
import LogInLayout from '../../layouts/LogInLayout'

const Login = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search.includes('+') ? search.replace('+', encodeURIComponent('+')) : search)
  const [userName, setUserName] = useState('')
  const [error, setError] = useState('')
  const [spin, setSpin] = useState(false)
  const [isShowCreateOneLink, setIsShowCreateOneLink] = useState(false)
  const [state] = useState(searchParams.get('state'))
  const [isLinkFlow] = useState(searchParams.get('isLinkFlow'))
  const [isAccountLinked] = useState(searchParams.get('isAccountLinked'))
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    if (location.state) {
      setSuccessMessage(t(location.state.state.successMessage))
      window.history.replaceState({}, document.title)
    }
  }, [])

  useEffect(() => {
    if (state != null) {
      const isOauthTrue = isOauthFlow(state)
      const decodedState = getDecodedState(state)
      const stateDecoded = atob(state)
      setIsShowCreateOneLink(showCreateOneLink(state))
      const loginHint = getValueFromState("login_hint=", stateDecoded)
      if(loginHint !== "" ) {
        setUserName(loginHint)
      }

      if (decodedState.includes('error=username_not_found')) {
        setError(t('login.loginUsernameNotFoundError'))
      }
      if (isOauthTrue) {
        if (Cookies.get(DCONE_LOCALE_COOKIE_NAME)) {
          i18n.changeLanguage(Cookies.get(DCONE_LOCALE_COOKIE_NAME)?.split('_')[0])
        }
      }
    } else {
      history.push(password)
    }
  }, [])

  const onUserNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUserName(e.target.value)
  }

  const onSignIn = (e):void => {
    e.preventDefault()
    setError('')
    if (userName === '') {
      setError("common.form.validation.requiredUserName")
      return
    }
    setSpin(true)
    verify(userName, state as unknown as string, 'en_US', '')
  }

  return (
    <LogInLayout>
      <section className={styles.login}>
        <h1 className={styles.heading}>{t('login.headline')}</h1>
        {isLinkFlow && isAccountLinked === 'true' &&
          <p className={styles.createAccountMessaging}>
            <a rel="noreferrer" href={`/imauth/ui/join-account`}>
              {t('login.dontHaveCredentials')}
            </a>
          </p>
        }
        {isLinkFlow && isAccountLinked === 'false' &&
          <p className={styles.createAccountMessaging}>
            <a rel="noreferrer" href={`/imauth/ui/create-account`}>
              {t('login.dontHaveAccount')}
            </a>
          </p>
        }
        {spin &&
          <div className={styles.spinner}>
            <Spinner />
          </div>
        }
        {!spin &&
          <Form center className={styles.form} onSubmit={(e) => {onSignIn(e)}}>
            {error !== '' &&
              <FormGroup>
                <PageLevelBanner
                  bannerType={PageLevelBannerType.ERROR}
                  description={error}
                />
              </FormGroup>
            }
            {successMessage !== '' &&
            <FormGroup>
              <PageLevelBanner
                bannerType={PageLevelBannerType.SUCCESS}
                description={successMessage}
              />
            </FormGroup>
          }

            <FormGroup>
              <Input
                className={styles.userNameInput}
                readOnly={!!searchParams.get('login_hint')}
                id='user-name'
                required
                label={t('username')}
                value={userName}
                pattern={USER_NAME_REGEXP}
                onChange={onUserNameChange}
                errors={{
                  valueMissing: t('required'),
                }}
              />
            </FormGroup>

            <div className={styles.actions}>
              <Button buttonType={ButtonType.PRIMARY} >{t('buttons.next')}</Button>
              <Link to={forgotUserName + `?state=${state}`}>
              {t('buttons.forgotUsername')}
            </Link>

            </div>
            {isShowCreateOneLink &&
                <p className={styles.createAccountMessaging}>
                  {t('login.dontHaveAnAccount')}
                    &nbsp;
                    <a rel="noreferrer" href= {`/account/create-user-account?state=${state}`} target="_blank">
                      {t('login.createOne')}
                    </a>
                </p>
               }
          </Form>
        }
      </section>
    </LogInLayout>
  )
}

export default Login